import type { ReactNode } from 'react'
import { HERO_SHELL, HERO_TONE, type HeroTone } from '@/components/loans/loanHero'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'

export interface GoalsHeroItem {
  label: string
  value: string
  tone?: HeroTone
  sub?: ReactNode
}

/** A mint hero with one headline figure, a progress bar (with an optional time-gone tick) and side figures. */
export function GoalsHero({
  ariaLabel,
  eyebrow,
  label,
  amount,
  note,
  percent,
  timeGone,
  items,
}: {
  ariaLabel: string
  eyebrow?: ReactNode
  label: string
  amount: number
  note?: ReactNode
  percent: number
  timeGone?: number | null
  items: GoalsHeroItem[]
}) {
  const { formatWhole: format } = useFormatCurrency()
  const wide = useMediaQuery(DESKTOP_QUERY)
  return (
    <section
      aria-label={ariaLabel}
      className={cn(HERO_SHELL, 'flex flex-col gap-2 motion-safe:animate-fade-slide-up md:gap-3')}
    >
      {eyebrow && (
        <p className="hidden text-[11.5px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)] md:block">
          {eyebrow}
        </p>
      )}
      <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-end md:justify-between md:gap-4">
        <div>
          <div className="flex items-baseline justify-between gap-3 md:block">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)] md:text-xs md:font-medium md:normal-case md:tracking-normal">
              {label}
            </p>
            <p
              data-testid="goals-hero-amount"
              className="whitespace-nowrap font-serif text-[22px] font-bold leading-tight tracking-tight tabular-nums md:text-[40px] md:font-semibold"
            >
              {format(amount)}
            </p>
          </div>
          {note && wide && (
            <p className="mt-0.5 text-[13px] tabular-nums text-[var(--fl-hero-fg-2)]">{note}</p>
          )}
        </div>
        {items.length > 0 && (
          <dl className="order-last flex gap-4 text-xs md:order-none md:gap-7">
            {items.map((item) => (
              <div key={item.label} className="min-w-0">
                <dt className="text-[11.5px] text-[var(--fl-hero-fg-2)] md:text-xs">
                  {item.label}
                </dt>
                <dd
                  className={cn(
                    'whitespace-nowrap text-[13px] font-semibold tabular-nums md:font-serif md:text-lg',
                    item.tone && HERO_TONE[item.tone]
                  )}
                >
                  {item.value}
                  {item.sub && (
                    <span className="ml-1 font-sans text-[11.5px] font-normal text-[var(--fl-hero-fg-2)] md:text-xs">
                      {item.sub}
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <div
          aria-hidden="true"
          className="relative h-2 w-full rounded-full bg-[var(--fl-hero-bar-faint)] md:h-2.5"
        >
          <div
            className="h-full rounded-full bg-[var(--fl-hero-bar)]"
            style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }}
          />
          {timeGone != null && (
            <span
              className="absolute -top-1 h-[calc(100%+8px)] w-0.5 rounded-full bg-[var(--fl-hero-fg)]"
              style={{ left: `${timeGone * 100}%` }}
            />
          )}
        </div>
        {note && !wide && <p className="text-xs tabular-nums text-[var(--fl-hero-fg-2)]">{note}</p>}
      </div>
    </section>
  )
}
